import { useCountries } from "@/pages/forms/countries";
/**
 * FishMapPage — interactive species × location map.
 *
 * Filters water type (All / Salt / Fresh), country/state/region/town,
 * and a fuzzy species autosuggest dropdown.  Renders Leaflet pins
 * (green = freshwater, blue = saltwater).  Auto-zooms to the active
 * geographic filter with a ~50km buffer (computed server-side).
 *
 * Below the map: a table of charter operators covering the active
 * region — every link is tracked via POST /api/fish-map/track-charter-view
 * which (rate-limited) fires a Resend email to the operator.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Loader2, Anchor, Fish, MapPin, X,
         Droplet, Waves, ExternalLink, Flame, Bell, Check, Plus } from "lucide-react";
import TackleLogo from "../components/tackle/TackleLogo";
import "leaflet/dist/leaflet.css";
import "./FishMapPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

// Lazy import leaflet to dodge SSR issues
let L = null;
const loadLeaflet = async () => {
  if (!L) L = (await import("leaflet")).default;
  return L;
};

const COUNTRIES = [
  { code: "AU", label: "🇦🇺 Australia" },
  { code: "NZ", label: "🇳🇿 New Zealand" },
  { code: "ALL", label: "🌍 All / Worldwide" },
];

export default function FishMapPage() {
  const sharedCountries=useCountries();
  const availableCountries=COUNTRIES.map(c=>{const match=sharedCountries.find(row=>row.code===c.code);return match?{...c,label:match.flag+" "+match.name}:c;});
  const [water, setWater] = useState("all");      // all | salt | fresh
  const [country, setCountry] = useState("AU");
  const [state, setState] = useState(null);
  const [region, setRegion] = useState(null);
  const [speciesQ, setSpeciesQ] = useState("");
  const [speciesSel, setSpeciesSel] = useState(null);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [states, setStates] = useState([]);
  const [regions, setRegions] = useState([]);
  const [spots, setSpots] = useState([]);
  const [bounds, setBounds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [charters, setCharters] = useState([]);
  const [inSeason, setInSeason] = useState(false);
  const [seasonSpecies, setSeasonSpecies] = useState([]); // hot list for chips
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState("");
  const [subscribeBusy, setSubscribeBusy] = useState(false);
  const [subscribeDone, setSubscribeDone] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitForm, setSubmitForm] = useState({
    species: "", water: "salt", state: "", region: "", town: "",
    season: "", notes: "", submitter_email: "", submitter_name: "",
    lat: "", lng: "",
  });
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const currentMonth = new Date().getMonth() + 1;
  const monthName = new Date().toLocaleString("en-AU", { month: "long" });

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Load filter taxonomy
  useEffect(() => {
    const c = country === "ALL" ? null : country;
    api.get(`/api/fish-map/states`, { params: c ? { country: c } : {} })
       .then(r => setStates(r.data || []));
  }, [country]);

  useEffect(() => {
    const c = country === "ALL" ? null : country;
    api.get(`/api/fish-map/regions`, {
      params: { ...(c ? { country: c } : {}), ...(state ? { state } : {}) },
    }).then(r => setRegions(r.data || []));
  }, [country, state]);

  // Fuzzy species autosuggest
  useEffect(() => {
    if (!speciesQ || speciesQ.length < 1) {
      setSuggestions([]); return;
    }
    const t = setTimeout(() => {
      api.get(`/api/fish-map/species`, { params: { q: speciesQ, limit: 10 }})
         .then(r => setSuggestions(r.data || []));
    }, 220);
    return () => clearTimeout(t);
  }, [speciesQ]);

  // Refetch spots whenever any filter changes
  useEffect(() => {
    setLoading(true);
    const params = {};
    if (water !== "all") params.water = water;
    if (country && country !== "ALL") params.country = country;
    if (state) params.state = state;
    if (region) params.region = region;
    if (speciesSel) params.species = speciesSel;
    if (inSeason) params.in_season = true;
    api.get(`/api/fish-map/spots`, { params }).then(r => {
      setSpots(r.data.results || []);
      setBounds(r.data.bounds || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [water, country, state, region, speciesSel, inSeason]);

  // In-season-now species chips (top hot list)
  useEffect(() => {
    api.get(`/api/fish-map/in-season-now`, { params: { limit: 14 }})
       .then(r => setSeasonSpecies(r.data.results || []))
       .catch(() => setSeasonSpecies([]));
  }, []);

  // Refetch charters that cover the active region
  useEffect(() => {
    if (!region) { setCharters([]); return; }
    // map region → region_slug used by hp_fishing_tours
    const slug = region.toLowerCase().replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-+|-+$/g, "");
    api.get(`/api/fish-map/charters-for-region`, { params: { region_slug: slug }})
       .then(r => setCharters(r.data || []))
       .catch(() => setCharters([]));
  }, [region]);

  // Init Leaflet map once
  useEffect(() => {
    let mounted = true;
    (async () => {
      const Lib = await loadLeaflet();
      if (!mounted || !mapEl.current || mapRef.current) return;
      const map = Lib.map(mapEl.current, {
        center: [-25.0, 134.0],   // Australia
        zoom: 4,
        zoomControl: true,
        scrollWheelZoom: true,
      });
      Lib.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        { maxZoom: 18,
          attribution: '© <a href="https://www.openstreetmap.org/">OSM</a>' }
      ).addTo(map);
      mapRef.current = map;
    })();
    return () => { mounted = false;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  // Render pins whenever spots change
  useEffect(() => {
    (async () => {
      const Lib = await loadLeaflet();
      const map = mapRef.current; if (!map) return;
      // wipe old pins
      markersRef.current.forEach(m => map.removeLayer(m));
      markersRef.current = [];

      spots.forEach((s) => {
        if (typeof s.lat !== "number" || typeof s.lng !== "number") return;
        const isFresh = s.water === "fresh";
        const colour = isFresh ? "#16a34a" : "#1aa3c7";
        const icon = Lib.divIcon({
          className: "fm-pin",
          html:
            `<span class="fm-pin__dot" style="background:${colour};
              box-shadow:0 0 0 4px ${colour}33,0 0 0 8px ${colour}11"></span>`,
          iconSize: [18, 18], iconAnchor: [9, 9],
        });
        const m = Lib.marker([s.lat, s.lng], { icon }).addTo(map);
        const popup = `
          <div class="fm-popup">
            <div class="fm-popup__species">${s.species}</div>
            <div class="fm-popup__where">
              ${s.town || ""}${s.region ? ` · ${s.region}` : ""}
              ${s.state ? ` · ${s.state}` : ""}
            </div>
            <div class="fm-popup__season">${s.season || ""}</div>
            <p class="fm-popup__notes">${s.notes || ""}</p>
          </div>`;
        m.bindPopup(popup);
        markersRef.current.push(m);
      });

      // Auto-zoom to bounds
      if (bounds) {
        map.fitBounds(
          [[bounds.min_lat, bounds.min_lng], [bounds.max_lat, bounds.max_lng]],
          { padding: [40, 40], maxZoom: 9 });
      } else if (!spots.length) {
        map.setView([-25, 134], 4);
      }
    })();
  }, [spots, bounds]);

  const onPickSpecies = (s) => {
    setSpeciesSel(s.species);
    setSpeciesQ(s.species);
    setShowSuggest(false);
  };

  const clearSpecies = () => { setSpeciesSel(null); setSpeciesQ(""); };

  const trackCharterClick = useCallback((charter) => {
    // Fire and forget — never block the link
    try {
      api.post(`/api/fish-map/track-charter-view`, {
        charter_slug: charter.slug,
        source: "fish-map",
        species: speciesSel || undefined,
        region: region || undefined,
      });
    } catch (_) { /* ignore */ }
  }, [speciesSel, region]);

  const submitSubscribe = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!speciesSel || !subscribeEmail.includes("@")) return;
    setSubscribeBusy(true);
    try {
      await api.post(`/api/fish-map/season-subscribe`, {
        email: subscribeEmail.trim(),
        species: speciesSel,
        region: region || undefined,
        state: state || undefined,
      });
      setSubscribeDone(true);
      setTimeout(() => {
        setSubscribeOpen(false); setSubscribeDone(false); setSubscribeEmail("");
      }, 2400);
    } catch (_) {
      // surface a soft error via the busy state release
    }
    setSubscribeBusy(false);
  }, [speciesSel, subscribeEmail, region, state]);

  const submitSpot = useCallback(async (e) => {
    e?.preventDefault?.();
    setSubmitError("");
    if (!submitForm.species || submitForm.species.trim().length < 2) {
      setSubmitError("Species is required (min 2 chars)."); return;
    }
    if (!submitForm.lat && !(submitForm.region && submitForm.town)) {
      setSubmitError("Provide coordinates OR region + town.");
      return;
    }
    setSubmitBusy(true);
    try {
      const payload = {
        species: submitForm.species.trim(),
        water: submitForm.water,
        state: submitForm.state || undefined,
        region: submitForm.region || undefined,
        town: submitForm.town || undefined,
        season: submitForm.season || undefined,
        notes: submitForm.notes || undefined,
        submitter_email: submitForm.submitter_email || undefined,
        submitter_name: submitForm.submitter_name || undefined,
        lat: submitForm.lat ? parseFloat(submitForm.lat) : undefined,
        lng: submitForm.lng ? parseFloat(submitForm.lng) : undefined,
      };
      await api.post(`/api/fish-map/submit-spot`, payload);
      setSubmitDone(true);
      setTimeout(() => {
        setSubmitOpen(false); setSubmitDone(false);
        setSubmitForm({
          species: "", water: "salt", state: "", region: "", town: "",
          season: "", notes: "", submitter_email: "", submitter_name: "",
          lat: "", lng: "",
        });
      }, 2800);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail
        || "Could not submit — please try again.");
    }
    setSubmitBusy(false);
  }, [submitForm]);

  const counts = useMemo(() => ({
    salt: spots.filter(s => s.water === "salt").length,
    fresh: spots.filter(s => s.water === "fresh").length,
  }), [spots]);

  return (
    <div className="fish-map" data-testid="fish-map-root">
      {/* Topbar — sister-site visual */}
      <header className="tackle-topbar">
        <a href="/tackle" className="tackle-topbar__brand">
          <TackleLogo />
          <span className="tackle-topbar__brandtext">
            Tackle<span className="tackle-topbar__dot">.</span>au
          </span>
        </a>
        <nav className="tackle-topbar__nav">
          <a href="/tackle">Shop</a>
          <a href="/fishing-tours">Charters</a>
          <a href="/fish-map" style={{ color: "#fbbf24" }}>Fish Map</a>
          <a href="/plaza/shop">Plaza</a>
        </nav>
        <a href="/fishing-tours" className="tackle-topbar__cart">Browse charters →</a>
      </header>

      {/* Hero strip */}
      <section className="fm-hero">
        <div className="fm-hero__copy">
          <span className="fm-hero__eyebrow">
            <MapPin size={14} /> Fish Finder — {spots.length} spots loaded
          </span>
          <h1 className="fm-hero__title">
            Where to fish.<br/>
            <span className="fm-hero__title-accent">By species, by state.</span>
          </h1>
          <p className="fm-hero__sub">
            {counts.salt} saltwater · {counts.fresh} freshwater pins
            across Australia, NZ and the world&apos;s best fisheries — including
            rivers, estuaries, reefs and impoundments.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <div className="fm-filters" data-testid="fm-filters">
        {/* In-season toggle — first, most prominent */}
        <button
          className={`fm-pill fm-pill--season ${inSeason ? "is-active" : ""}`}
          onClick={() => setInSeason(s => !s)}
          data-testid="fm-in-season-toggle"
          aria-pressed={inSeason}
          title={`Show only species in season this ${monthName}`}
        >
          <Flame size={14}/> In season{inSeason ? "" : "?"}
        </button>

        {/* Water type */}
        <div className="fm-filter-group" role="radiogroup" aria-label="Water type">
          {[
            { v: "all",   label: "All",        Icon: Waves },
            { v: "salt",  label: "Saltwater",  Icon: Anchor },
            { v: "fresh", label: "Freshwater", Icon: Droplet },
          ].map(({ v, label, Icon }) => (
            <button
              key={v}
              className={`fm-pill ${water === v ? "is-active" : ""} fm-pill--${v}`}
              onClick={() => setWater(v)}
              data-testid={`fm-water-${v}`}
              aria-pressed={water === v}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Country */}
        <select
          className="fm-select"
          value={country}
          onChange={(e) => { setCountry(e.target.value); setState(null); setRegion(null); }}
          data-testid="fm-country"
        >
          {availableCountries.map(c => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>

        {/* State */}
        <select
          className="fm-select"
          value={state || ""}
          onChange={(e) => { setState(e.target.value || null); setRegion(null); }}
          data-testid="fm-state"
        >
          <option value="">All states</option>
          {states.map(s => (
            <option key={s.state} value={s.state}>
              {s.state} ({s.count})
            </option>
          ))}
        </select>

        {/* Region */}
        <select
          className="fm-select"
          value={region || ""}
          onChange={(e) => setRegion(e.target.value || null)}
          data-testid="fm-region"
        >
          <option value="">All regions</option>
          {regions.map(r => (
            <option key={`${r.state}-${r.region}`} value={r.region}>
              {r.region}{r.state ? ` (${r.state})` : ""} — {r.count}
            </option>
          ))}
        </select>

        {/* Species fuzzy search */}
        <div className="fm-species" data-testid="fm-species">
          <Search size={15} />
          <input
            placeholder="Species (e.g. barramundi, marlin, snappa)…"
            value={speciesQ}
            onChange={(e) => { setSpeciesQ(e.target.value); setShowSuggest(true);
              if (!e.target.value) setSpeciesSel(null); }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
            data-testid="fm-species-input"
          />
          {speciesQ && (
            <button className="fm-species__clear" onClick={clearSpecies}
                    data-testid="fm-species-clear" aria-label="Clear species">
              <X size={14} />
            </button>
          )}
          {showSuggest && suggestions.length > 0 && (
            <div className="fm-species__suggest" role="listbox">
              {suggestions.map((s) => (
                <button
                  key={s.species_norm}
                  className="fm-species__option"
                  onClick={() => onPickSpecies(s)}
                  data-testid={`fm-species-option-${s.species_norm.replace(/\s/g,'-')}`}
                  role="option"
                >
                  <span className={`fm-species__dot fm-species__dot--${s.water || 'salt'}`}/>
                  <span className="fm-species__name">{s.species}</span>
                  <span className="fm-species__count">
                    {s.fuzzy ? "fuzzy" : `${s.count || 0} spots`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* In-season-now chip rail — auto-detected from current month */}
      {seasonSpecies.length > 0 && (
        <div className="fm-season-rail" data-testid="fm-season-rail">
          <span className="fm-season-rail__label">
            <Flame size={13}/> On the chew this {monthName}:
          </span>
          {seasonSpecies.slice(0, 10).map(s => (
            <button
              key={s.species_norm}
              className={`fm-season-rail__chip
                          fm-season-rail__chip--${s.water || 'salt'}
                          ${speciesSel === s.species ? 'is-active' : ''}`}
              onClick={() => { setSpeciesSel(s.species); setSpeciesQ(s.species); }}
              data-testid={`fm-season-chip-${s.species_norm.replace(/\s/g,'-')}`}
            >
              {s.species}
              <span className="fm-season-rail__chip-count">{s.count}</span>
            </button>
          ))}
          <button
            className="fm-season-rail__notify"
            onClick={() => setSubscribeOpen(true)}
            data-testid="fm-subscribe-open"
            disabled={!speciesSel}
            title={speciesSel
              ? `Get an alert when ${speciesSel} comes on the chew`
              : "Pick a species first to set up alerts"}
          >
            <Bell size={13}/> {speciesSel
              ? `Notify me when ${speciesSel} fires`
              : "Pick a species to notify me"}
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="fm-legend">
        <span><span className="fm-legend__dot fm-legend__dot--salt"/> Saltwater</span>
        <span><span className="fm-legend__dot fm-legend__dot--fresh"/> Freshwater</span>
        <span className="fm-legend__hint">
          Click a pin for season, target species &amp; local notes
        </span>
      </div>

      {/* Map */}
      <div className="fm-map-wrap">
        <div ref={mapEl} className="fm-map" data-testid="fish-map-canvas"/>
        {loading && (
          <div className="fm-map__loading"><Loader2 className="fm-spin" size={28}/> Loading pins…</div>
        )}
      </div>

      {/* Charter operators covering this area */}
      <section className="fm-charters" data-testid="fm-charters" id="fm-charters-for-region">
        <div className="fm-charters__head">
          <h2>
            <Fish size={18}/> Charters covering this area
            {region && <span className="fm-charters__regiontag">{region}</span>}
          </h2>
          <Link to="/fishing-tours" className="fm-charters__all">
            Browse all 50 operators →
          </Link>
        </div>
        {!region ? (
          <p className="fm-charters__empty">
            Pick a region above to see local charter operators.
          </p>
        ) : charters.length === 0 ? (
          <p className="fm-charters__empty">
            No charters claimed for <strong>{region}</strong> yet.
            <Link to="/fishing-tours"> View nearby operators →</Link>
          </p>
        ) : (
          <table
            className="fm-charters__table"
            data-testid="fm-charters-for-region"
          >
            <thead>
              <tr>
                <th>Operator</th>
                <th>Targets</th>
                <th>From</th>
                <th>Status</th>
                <th>Visit</th>
              </tr>
            </thead>
            <tbody>
              {charters.map(c => (
                <tr key={c.slug} data-testid={`fm-charter-row-${c.slug}`}>
                  <td>
                    <strong>{c.name}</strong>
                    <div className="fm-charters__where">
                      <MapPin size={11}/> {c.region}{c.state ? `, ${c.state}` : ""}
                    </div>
                  </td>
                  <td className="fm-charters__species">
                    {c.target_species || c.specialty}
                  </td>
                  <td>
                    {c.price_from_aud
                      ? `$${c.price_from_aud.toLocaleString()}`
                      : "—"}
                  </td>
                  <td>
                    <span className={`fm-charters__chip fm-charters__chip--${
                      c.claimed_at ? "claimed" : "unclaimed"}`}>
                      {c.claimed_at ? "✓ Claimed" : "Unclaimed"}
                    </span>
                  </td>
                  <td>
                    <Link
                      to={`/fishing-tours/${c.slug}`}
                      className="fm-charters__btn"
                      onClick={() => trackCharterClick(c)}
                      data-testid={`fm-charter-link-${c.slug}`}
                    >
                      View <ExternalLink size={12}/>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <footer className="fm-footer">
        Hustle Fish Map · Spots crowdsourced &amp; verified.
        <button
          type="button"
          className="fm-footer__submit"
          onClick={() => setSubmitOpen(true)}
          data-testid="fm-submit-spot-open"
        >
          <Plus size={13}/> Submit your own spot
        </button>
        <Link
          to="/fish-map/hall-of-fame"
          className="fm-footer__hof"
          data-testid="fm-hall-of-fame-link"
        >
          🏆 Hall of Fame →
        </Link>
      </footer>

      {/* Season subscribe modal */}
      {subscribeOpen && (
        <div
          className="fm-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => !subscribeBusy && setSubscribeOpen(false)}
          data-testid="fm-subscribe-modal"
        >
          <form
            className="fm-modal__card"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitSubscribe}
          >
            <button
              type="button"
              className="fm-modal__close"
              onClick={() => setSubscribeOpen(false)}
              aria-label="Close"
              data-testid="fm-subscribe-close"
            >
              <X size={18}/>
            </button>
            {subscribeDone ? (
              <div className="fm-modal__done" data-testid="fm-subscribe-done">
                <Check size={32}/>
                <h3>You&apos;re in.</h3>
                <p>We&apos;ll email you the moment <strong>{speciesSel}</strong>
                {region ? ` in ${region}` : state ? ` in ${state}` : ""} is on the chew.</p>
              </div>
            ) : (
              <>
                <div className="fm-modal__head">
                  <Bell size={18}/>
                  <h3>Season alerts for {speciesSel || "this species"}</h3>
                </div>
                <p className="fm-modal__sub">
                  We&apos;ll send one short email the moment
                  <strong> {speciesSel || "your target species"}</strong>
                  {region ? ` in ${region}` : state ? ` in ${state}` : ""}
                   comes into season. No spam, ever.
                </p>
                <label className="fm-modal__field">
                  <span>Your email</span>
                  <input
                    type="email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder="you@email.com"
                    required
                    data-testid="fm-subscribe-email"
                    autoFocus
                  />
                </label>
                <button
                  type="submit"
                  className="fm-modal__submit"
                  disabled={subscribeBusy || !subscribeEmail.includes("@")}
                  data-testid="fm-subscribe-submit"
                >
                  {subscribeBusy ? <Loader2 className="fm-spin" size={15}/> : <Bell size={14}/>}
                  {subscribeBusy ? "Subscribing…" : "Notify me when in season"}
                </button>
                <p className="fm-modal__legal">
                  By subscribing you agree to receive seasonal fishing alerts
                  from Hustle Group Pty Ltd. Unsubscribe any time.
                </p>
              </>
            )}
          </form>
        </div>
      )}

      {/* Submit-a-spot modal */}
      {submitOpen && (
        <div
          className="fm-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => !submitBusy && setSubmitOpen(false)}
          data-testid="fm-submit-spot-modal"
        >
          <form
            className="fm-modal__card fm-modal__card--wide"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitSpot}
          >
            <button
              type="button"
              className="fm-modal__close"
              onClick={() => setSubmitOpen(false)}
              aria-label="Close"
              data-testid="fm-submit-spot-close"
            >
              <X size={18}/>
            </button>
            {submitDone ? (
              <div className="fm-modal__done" data-testid="fm-submit-spot-done">
                <Check size={32}/>
                <h3>In the queue.</h3>
                <p>Thanks — your <strong>{submitForm.species}</strong> spot
                  will be on the map within 48 hours after review.</p>
              </div>
            ) : (
              <>
                <div className="fm-modal__head">
                  <Plus size={18}/>
                  <h3>Submit a fishing spot</h3>
                </div>
                <p className="fm-modal__sub">
                  Share a spot you fish — once verified by our team it&apos;ll
                  appear on the public map.
                </p>
                <div className="fm-modal__grid">
                  <label className="fm-modal__field">
                    <span>Species *</span>
                    <input
                      value={submitForm.species}
                      onChange={(e) => setSubmitForm(s => ({...s, species: e.target.value}))}
                      placeholder="Barramundi"
                      required
                      data-testid="fm-submit-species"
                    />
                  </label>
                  <label className="fm-modal__field">
                    <span>Water</span>
                    <select
                      value={submitForm.water}
                      onChange={(e) => setSubmitForm(s => ({...s, water: e.target.value}))}
                      data-testid="fm-submit-water"
                    >
                      <option value="salt">Saltwater</option>
                      <option value="fresh">Freshwater</option>
                    </select>
                  </label>
                  <label className="fm-modal__field">
                    <span>State</span>
                    <select
                      value={submitForm.state}
                      onChange={(e) => setSubmitForm(s => ({...s, state: e.target.value}))}
                      data-testid="fm-submit-state"
                    >
                      <option value="">—</option>
                      <option value="QLD">QLD</option>
                      <option value="NSW">NSW</option>
                      <option value="VIC">VIC</option>
                      <option value="WA">WA</option>
                      <option value="SA">SA</option>
                      <option value="TAS">TAS</option>
                      <option value="NT">NT</option>
                      <option value="ACT">ACT</option>
                    </select>
                  </label>
                  <label className="fm-modal__field">
                    <span>Region</span>
                    <input
                      value={submitForm.region}
                      onChange={(e) => setSubmitForm(s => ({...s, region: e.target.value}))}
                      placeholder="Mary River"
                      data-testid="fm-submit-region"
                    />
                  </label>
                  <label className="fm-modal__field fm-modal__field--full">
                    <span>Town / waterbody</span>
                    <input
                      value={submitForm.town}
                      onChange={(e) => setSubmitForm(s => ({...s, town: e.target.value}))}
                      placeholder="Tin Can Bay / Mary River mouth"
                      data-testid="fm-submit-town"
                    />
                  </label>
                  <label className="fm-modal__field">
                    <span>Latitude</span>
                    <input
                      type="number"
                      step="0.001"
                      value={submitForm.lat}
                      onChange={(e) => setSubmitForm(s => ({...s, lat: e.target.value}))}
                      placeholder="-25.917"
                      data-testid="fm-submit-lat"
                    />
                  </label>
                  <label className="fm-modal__field">
                    <span>Longitude</span>
                    <input
                      type="number"
                      step="0.001"
                      value={submitForm.lng}
                      onChange={(e) => setSubmitForm(s => ({...s, lng: e.target.value}))}
                      placeholder="153.012"
                      data-testid="fm-submit-lng"
                    />
                  </label>
                  <label className="fm-modal__field fm-modal__field--full">
                    <span>Season</span>
                    <input
                      value={submitForm.season}
                      onChange={(e) => setSubmitForm(s => ({...s, season: e.target.value}))}
                      placeholder="Oct–May"
                      data-testid="fm-submit-season"
                    />
                  </label>
                  <label className="fm-modal__field fm-modal__field--full">
                    <span>Local notes</span>
                    <textarea
                      rows={3}
                      value={submitForm.notes}
                      onChange={(e) => setSubmitForm(s => ({...s, notes: e.target.value}))}
                      placeholder="Set witches' hats in mangrove creeks on a making tide…"
                      data-testid="fm-submit-notes"
                    />
                  </label>
                  <label className="fm-modal__field">
                    <span>Your name</span>
                    <input
                      value={submitForm.submitter_name}
                      onChange={(e) => setSubmitForm(s => ({...s, submitter_name: e.target.value}))}
                      placeholder="(optional)"
                      data-testid="fm-submit-name"
                    />
                  </label>
                  <label className="fm-modal__field">
                    <span>Your email</span>
                    <input
                      type="email"
                      value={submitForm.submitter_email}
                      onChange={(e) => setSubmitForm(s => ({...s, submitter_email: e.target.value}))}
                      placeholder="(optional — for credit)"
                      data-testid="fm-submit-email"
                    />
                  </label>
                </div>
                {submitError && (
                  <p className="fm-modal__error" data-testid="fm-submit-error">
                    {submitError}
                  </p>
                )}
                <button
                  type="submit"
                  className="fm-modal__submit"
                  disabled={submitBusy}
                  data-testid="fm-submit-spot-submit"
                >
                  {submitBusy ? <Loader2 className="fm-spin" size={15}/> : <Plus size={14}/>}
                  {submitBusy ? "Submitting…" : "Submit my spot"}
                </button>
                <p className="fm-modal__legal">
                  Submissions are reviewed before going live. By submitting
                  you grant Hustle Group Pty Ltd a perpetual licence to display
                  the spot on the Fish Map.
                </p>
              </>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
